import * as React from "react";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ClientDetails } from "../../../../../../../Wrap/Models/Models/Processes/Quote/ClientDetails";
import { CheckboxGroup } from "../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader } from "../../../NewBusinessWizard/Components/SharedComponents";
import { isTaxRelifAvailable } from "../../../NewQuoteWizard/Components/Shared/Utils";
import { PaymentType, processTypeEnum } from "../../Resource/Contents";
import { RegularPaymentDetail } from "./RegularPaymentDetail";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { RegularDepositMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/RegularDepositMethod";
import { WizardLinter, EWizardLinterStatus } from "../../../../../../Shared/Process/Process/WizardLinter";
import { EWizardLinterHelper } from "../../../NewBusinessWizard/Resource/EWizardLinterHelper";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { DateHelper } from "../../../../../../../react-core/Helpers/DateHelper";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { User } from "../../../../../../../Wrap/Models/Models/User/User";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../../Shared/Common/UserMessages";
import { UserStore } from "../../../../../Stroes/Variable";
import { RestrictionStore } from "../../Restrictions/Variable";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { RestrictionHelper } from "../../../../../../Shared/Common/RestrictionHelper";
import { RestrictionAction } from "../../Restrictions/RestrictionAction";

interface IRegularPaymentProps extends IProcessComponentProps {
    clientInfo: ClientDetails;
    checkBoxOption: Array<PaymentType>;
    subAccountType: SubAccountType;
    hasIndexation?: boolean;
    hasContinue?: boolean;
    hasPaymentMethod?: boolean;
    bankAccounts?: BankAccount[];
    bankAccountInstructions?: BankAccountInstruction[];
    processType: processTypeEnum;
    isEdit?: boolean;
    HasExistingSIPP?: boolean;
    onSaveNewBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    onRemoveBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    inUseBankAccountInstruction?: string[];
    autoLineCompleted?: boolean;
}

interface IRegularPaymentState {
    userInfo: User;
}

export class RegularPayment extends ProcessComponent<IRegularPaymentProps, IRegularPaymentState> {
    public static defaultProps = {
        autoLineCompleted: true,
    };
    constructor(props: any) {
        super(props);
        this.state = {
            userInfo: UserStore.getUserInfo(),
        };
    }
    private getKey = (key: PaymentType) => {
        switch (key) {
            case PaymentType.Client:
                return "ClientRegularPayment";
            case PaymentType.ThirdParty:
                return "ThirdPartyRegularPayment";
            case PaymentType.Employer:
                return "EmployerRegularPayment";
        }
    };

    componentDidMount() {
        super.componentDidMount();
        if (RestrictionStore.getData() == null) {
            if (!CommonHelper.isNullOrEmpty(getQueryParameters()["subaccounthierarchyid"])) {
                RestrictionAction.getRestrictionByHierarchyId(getQueryParameters()["subaccounthierarchyid"]);
            } else if (!CommonHelper.isNullOrEmpty(getQueryParameters()["hierarchyid"])) {
                RestrictionAction.getRestrictionByHierarchyId(getQueryParameters()["hierarchyid"]);
            }
        }
    }

    private getOptions = () => {
        const { checkBoxOption, data, onChange, processType, isEdit } = this.props;
        let isRestrictionActive = false;
        if (RestrictionHelper.shouldHandleRestriction(processType)) {
            isRestrictionActive = RestrictionStore.depositDisabled();
        }
        const isNone = checkBoxOption.map((type) => data[this.getKey(type)] == null).every((ele) => ele == true);
        const empty = {};
        checkBoxOption.forEach((type) => {
            empty[this.getKey(type)] = null;
        });
        const options = [
            {
                label: "None",
                value: isNone,
                disabled: isRestrictionActive || this.props.isEdit || !this.props.autoLineCompleted || processType == processTypeEnum.Quote,
                onChange: (v) => onChange(empty),
            },
        ];
        checkBoxOption.forEach((type) => {
            options.push({
                label: type,
                value: data[this.getKey(type)] != null,
                disabled:
                    ((this.props.processType == processTypeEnum.NewBusinessExistingClient ||
                        this.props.processType == processTypeEnum.NewQuoteExistingClient) &&
                        this.props.HasExistingSIPP == true) ||
                    isRestrictionActive ||
                    (data[this.getKey(type)] && data[this.getKey(type)]?.RegularBuyId) ||
                    !this.props.autoLineCompleted,
                onChange: (v) => {
                    if (processType == processTypeEnum.Quote) {
                        onChange({
                            [this.getKey(type)]: v
                                ? {
                                      TaxRelief: isTaxRelifAvailable(this.props.clientInfo?.DateOfBirth, new Date()),
                                      RegularPaymentMethod: RegularDepositMethod.NotSpecified,
                                      RegularBuyId: null,
                                  }
                                : null,
                        });
                    } else {
                        onChange({
                            [this.getKey(type)]: v
                                ? {
                                      TaxRelief: isTaxRelifAvailable(this.props.clientInfo?.DateOfBirth, new Date()),
                                      RegularPaymentMethod: RegularDepositMethod.DirectDebit,
                                      RegularBuyId: null,
                                  }
                                : null,
                        });
                    }
                },
            });
        });

        return options;
    };

    private getProps = (key) => {
        const {
            data,
            onChange,
            subAccountType,
            clientInfo,
            bankAccounts,
            bankAccountInstructions,
            hasPaymentMethod,
            processType,
            isReview,
            onSaveNewBankAccountInstruction,
            onRemoveBankAccountInstruction,
            inUseBankAccountInstruction,
        } = this.props;
        return {
            clientInfo: clientInfo,
            label: key,
            type: key,
            data: data[this.getKey(key)],
            onChange: (changes) => this.onDataChange(onChange, changes, data, this.getKey(key)),
            onRef: (ref) => this.appendChild(ref),
            subAccountType: subAccountType,
            bankAccounts: bankAccounts,
            bankAccountInstructions: bankAccountInstructions,
            hasPaymentMethod: hasPaymentMethod,
            processType: processType,
            isReview: isReview,
            onSaveNewBankAccountInstruction: onSaveNewBankAccountInstruction,
            showErrorMsgPopUp: this.props.showErrorMsgPopUp,
            onRemoveBankAccountInstruction: onRemoveBankAccountInstruction,
            inUseBankAccountInstruction: inUseBankAccountInstruction,
        };
    };

    public getNextPaymentDate = (firstPaymentDate, nextPaymentDate) => {
        const first = JsonDateParser.toDate(firstPaymentDate);
        const next = JsonDateParser.toDate(nextPaymentDate);
        const useDate = first && first > new Date() ? first : next;
        return useDate ? DateHelper.getDayDifference(JsonDateParser.toDate(useDate), new Date()) : -1;
    };

    public render(): JSX.Element {
        const { data, subAccountType, isEdit, processType } = this.props;
        let isRestrictionActive = false;
        if (RestrictionHelper.shouldHandleRestriction(processType)) {
            isRestrictionActive = RestrictionStore.depositDisabled();
        }
        const clientNextPaymentdate = this.getNextPaymentDate(
            data?.ClientRegularPayment?.FirstCollectionDate,
            data?.ClientRegularPayment?.NextPaymentDate
        );

        const thirdPartyNextPaymentdate = this.getNextPaymentDate(
            data?.ThirdPartyRegularPayment?.FirstCollectionDate,
            data?.ThirdPartyRegularPayment?.NextPaymentDate
        );
        const employerNextPaymentdate = this.getNextPaymentDate(
            data?.EmployerRegularPayment?.FirstCollectionDate,
            data?.EmployerRegularPayment?.NextPaymentDate
        );

        let restrictedMessage = "An account with suspended or restricted activities cannot receive regular payment";

        if (this.state.userInfo != null && this.state.userInfo.IsAdviser) {
            restrictedMessage = GENERIC_RESTRICTIONS_MESSAGE;
        }

        return (
            <WizardLinter
                status={EWizardLinterStatus.PleaseCheck}
                enabled={EWizardLinterHelper.shouldDisplay(subAccountType)}
            >
                {SectionHeader(
                    "Regular payment",
                    isEdit ? "More than one payer type can be selected in this instruction." : ""
                )}
                {isRestrictionActive && <Alert alertType={AlertType.Error} message={restrictedMessage} />}
                <CheckboxGroup type="150" wrapCss="square-checkbox" options={this.getOptions()} />
                {clientNextPaymentdate != -1 &&
                    clientNextPaymentdate <= 10 &&
                    this.props.processType === processTypeEnum.RegularPayment && (
                        <Alert
                            alertType={AlertType.Warning}
                            message={`Please note the next collection date is within 10 days so cannot be edited.`}
                        />
                    )}
                {data.ClientRegularPayment && (
                    <RegularPaymentDetail
                        {...this.getProps(PaymentType.Client)}
                        readonly={
                            (clientNextPaymentdate != -1 &&
                                clientNextPaymentdate <= 10 &&
                                this.props.processType === processTypeEnum.RegularPayment) ||
                            this.props.autoLineCompleted === false
                        }
                        autoLineCompleted={this.props.autoLineCompleted}
                    />
                )}
                {thirdPartyNextPaymentdate != -1 &&
                    thirdPartyNextPaymentdate <= 10 &&
                    this.props.processType === processTypeEnum.RegularPayment && (
                        <Alert
                            alertType={AlertType.Warning}
                            message={`Please note the next collection date is within 10 days so cannot be edited.`}
                        />
                    )}
                {data.ThirdPartyRegularPayment && (
                    <RegularPaymentDetail
                        {...this.getProps(PaymentType.ThirdParty)}
                        readonly={
                            (thirdPartyNextPaymentdate != -1 &&
                                thirdPartyNextPaymentdate <= 10 &&
                                this.props.processType === processTypeEnum.RegularPayment) ||
                            this.props.autoLineCompleted === false
                        }
                        autoLineCompleted={this.props.autoLineCompleted}
                    />
                )}
                {employerNextPaymentdate != -1 &&
                    employerNextPaymentdate <= 10 &&
                    this.props.processType === processTypeEnum.RegularPayment && (
                        <Alert
                            alertType={AlertType.Warning}
                            message={`Please note the next collection date is within 10 days so cannot be edited.`}
                        />
                    )}
                {data.EmployerRegularPayment && (
                    <RegularPaymentDetail
                        {...this.getProps(PaymentType.Employer)}
                        readonly={
                            (employerNextPaymentdate != -1 &&
                                employerNextPaymentdate <= 10 &&
                                this.props.processType === processTypeEnum.RegularPayment) ||
                            this.props.autoLineCompleted === false
                        }
                        autoLineCompleted={this.props.autoLineCompleted}
                    />
                )}
            </WizardLinter>
        );
    }
}
